import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { complaints } from "@/data/complaints";
import { localizeComplaint } from "@/data/complaints.en";
import { useLang, useT, useLocalePath } from "@/i18n";
import { useSeo, absoluteUrl } from "@/lib/seo";
import { ArrowRight, CalendarCheck, CheckCircle2, Stethoscope } from "lucide-react";

/** Slug -> lesbarer Linktext (vermeidet generische, unleserliche Linktexte) */
const postLabel = (slug: string) =>
  slug
    .split("-")
    .map((w) => (w.length > 3 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ")
    .replace(/^./, (c) => c.toUpperCase());


const BeschwerdeDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const lang = useLang();
  const lp = useLocalePath();
  const base = complaints.find((c) => c.slug === slug);
  const complaint = base ? localizeComplaint(base, lang) : undefined;
  const t = useT(
    {
      home: "Startseite",
      complaints: "Beschwerden",
      breadcrumbLabel: "Brotkrumen",
      signs: "Typische Anzeichen",
      approach: "Wie ich in der Praxis vorgehe",
      faq: "Häufige Fragen",
      related: "Passende Beiträge im Magazin",
      recognise: "Sie erkennen sich wieder?",
      ctaText:
        "In einem persönlichen Gespräch sehen wir uns Ihre Vorbefunde und Ihre Geschichte in Ruhe an – in der Praxis in Wien oder online.",
      contact: "Kontakt aufnehmen",
      services: "Leistungen ansehen",
      book: "Termin buchen",
      bookTop: "Termin buchen",
      topNote: "139 € · ca. 60 Minuten · Wien (Zoom n. Vereinbarung)",
      fallbackTitle: "Beschwerdebild | PRAXIS IB",
      inLanguage: "de-AT",
    },
    {
      home: "Home",
      complaints: "Health concerns",
      breadcrumbLabel: "Breadcrumb",
      signs: "Typical signs",
      approach: "How I work in my practice",
      faq: "Frequently asked questions",
      related: "Related articles in the magazine",
      recognise: "Does this sound familiar?",
      ctaText:
        "In a personal consultation we take a calm look at your previous findings and your history – at the practice in Vienna or online.",
      contact: "Get in touch",
      services: "View services",
      book: "Book an appointment",
      bookTop: "Book an appointment",
      topNote: "€139 · approx. 60 minutes · Vienna (Zoom by arrangement)",
      fallbackTitle: "Health concern | PRAXIS IB",
      inLanguage: "en",
    },
  );

  const jsonLd = useMemo(() => {
    if (!complaint) return null;
    const url = absoluteUrl(`/beschwerden/${complaint.slug}`);
    return [
      {
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        name: complaint.title,
        description: complaint.metaDescription,
        url,
        inLanguage: t.inLanguage,
        about: {
          "@type": "MedicalCondition",
          name: complaint.shortTitle,
          alternateName: complaint.synonyms,
        },
        author: { "@id": absoluteUrl("/#therapeutin") },
        publisher: { "@id": absoluteUrl("/#business") },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: complaint.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: t.home, item: absoluteUrl("/") },
          { "@type": "ListItem", position: 2, name: t.complaints, item: absoluteUrl("/beschwerden") },
          { "@type": "ListItem", position: 3, name: complaint.shortTitle, item: url },
        ],
      },
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complaint, lang]);

  useSeo({
    title: complaint?.metaTitle ?? t.fallbackTitle,
    description: complaint?.metaDescription,
    keywords: complaint ? [...complaint.keywords, ...complaint.synonyms].join(", ") : undefined,
    canonical: complaint ? `/beschwerden/${complaint.slug}` : "/beschwerden",
    jsonLd,
    jsonLdId: "beschwerde-jsonld",
  });

  if (!complaint) return <Navigate to={lp("/beschwerden")} replace />;

  const related = complaint.relatedPosts;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="py-12 md:py-16 bg-secondary/30">
          <div className="container mx-auto px-4 max-w-3xl">
            <nav aria-label={t.breadcrumbLabel} className="text-sm text-muted-foreground mb-6">
              <Link to={lp("/")} className="hover:text-primary">{t.home}</Link>
              <span className="mx-2">/</span>
              <Link to={lp("/beschwerden")} className="hover:text-primary">{t.complaints}</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground">{complaint.shortTitle}</span>
            </nav>
            <h1 className="text-3xl md:text-4xl font-light text-foreground mb-4">{complaint.title}</h1>
            <p className="text-muted-foreground leading-relaxed">{complaint.intro}</p>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
              <Button asChild>
                <Link to={lp("/termin")}>
                  <CalendarCheck className="h-4 w-4 mr-2" />
                  {t.bookTop}
                </Link>
              </Button>
              <span className="text-sm text-muted-foreground">{t.topNote}</span>
            </div>
          </div>
        </section>


        <article className="py-12 md:py-16">
          <div className="container mx-auto px-4 max-w-3xl space-y-10">
            {complaint.sections.map((s) => (
              <section key={s.heading}>
                <h2 className="text-xl md:text-2xl font-medium text-foreground mb-3">{s.heading}</h2>
                <div className="space-y-4">
                  {s.paragraphs.map((p, i) => (
                    <p key={i} className="text-muted-foreground leading-relaxed">{p}</p>
                  ))}
                </div>
              </section>
            ))}

            <section>
              <h2 className="text-xl md:text-2xl font-medium text-foreground mb-4">{t.signs}</h2>
              <ul className="space-y-2">
                {complaint.signs.map((s) => (
                  <li key={s} className="flex gap-3 text-muted-foreground">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-medium text-foreground mb-4">{t.approach}</h2>
              <ol className="space-y-3">
                {complaint.approach.map((a, i) => (
                  <li key={a} className="flex gap-3 text-muted-foreground">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                      {i + 1}
                    </span>
                    <span>{a}</span>
                  </li>
                ))}
              </ol>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-medium text-foreground mb-4">{t.faq}</h2>
              <div className="space-y-5">
                {complaint.faq.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-medium text-foreground mb-1">{f.q}</h3>
                    <p className="text-muted-foreground leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {related.length > 0 && (
              <section>
                <h2 className="text-xl md:text-2xl font-medium text-foreground mb-4">{t.related}</h2>
                <ul className="space-y-2">
                  {related.map((s) => (
                    <li key={s}>
                      <Link to={lp(`/blog/${s}`)} className="inline-flex items-center gap-2 text-primary hover:gap-3 transition-all">
                        <ArrowRight className="h-4 w-4" />
                        <span>{postLabel(s)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="p-6 space-y-3">
                <div className="flex items-center gap-2 text-primary">
                  <Stethoscope className="h-5 w-5" />
                  <span className="font-medium">{t.recognise}</span>
                </div>
                <p className="text-sm text-muted-foreground">{t.ctaText}</p>
                <div className="flex flex-wrap gap-3 pt-1">
                  <Button asChild>
                    <Link to={lp("/termin")}>
                      <CalendarCheck className="h-4 w-4 mr-2" />
                      {t.book}
                    </Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to={lp("/") + "#kontakt"}>{t.contact}</Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to={lp("/") + "#fachlicher-ansatz"}>{t.services}</Link>
                  </Button>
                </div>

              </CardContent>
            </Card>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BeschwerdeDetail;
